import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, updatePassword } from '../lib/supabase';

export function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);

  useEffect(() => {
    // Check if we have a valid session from the reset link
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setIsValidToken(false);
        setError('Invalid or expired reset link. Please request a new one.');
      } else {
        setIsValidToken(true);
      }
    };
    checkSession();
  }, []);

  const validatePassword = (pass: string): string | null => {
    if (pass.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await updatePassword(password);
      setSuccess(true);
      // Sign out after password reset to require re-login
      await supabase.auth.signOut();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We couldn\u0027t update your password right now. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  // Still checking for a valid session token
  if (isValidToken === null) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-5">
        <div className="max-w-sm w-full text-center">
          <p className="text-sm text-ink-faint" style={{ fontWeight: 300 }}>Verifying reset link&hellip;</p>
        </div>
      </div>
    );
  }

  // No valid recovery token — user navigated here directly or link expired
  if (isValidToken === false) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-5">
        <div className="max-w-sm w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-rose/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-rose"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="font-sans text-2xl text-ink mb-2" style={{ fontWeight: 600 }}>
              No reset link found
            </h2>
            <p className="text-sm text-ink-faint mb-6" style={{ fontWeight: 300 }}>
              This page requires a password reset link from your email. If your link expired, you can request a new one.
            </p>
            <button onClick={() => navigate('/forgot-password')} className="btn-rose text-sm">
              Request new link
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-5">
        <div className="max-w-sm w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="font-sans text-2xl text-ink mb-2" style={{ fontWeight: 600 }}>
              Password updated
            </h2>
            <p className="text-sm text-ink-faint mb-6" style={{ fontWeight: 300 }}>
              Your password has been successfully reset. Please sign in with your new password.
            </p>
            <button onClick={() => navigate('/')} className="btn-rose text-sm">
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-5">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <h2 className="font-sans text-3xl text-ink mb-2" style={{ fontWeight: 600 }}>
            Create new password
          </h2>
          <p className="text-sm text-ink-faint" style={{ fontWeight: 300 }}>
            Enter a new password for your account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="password"
              className="block text-[11px] font-sans text-ink-faint mb-1.5 tracking-wider uppercase"
            >
              New password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="6+ characters"
              className="input"
              disabled={loading || isValidToken === null}
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-[11px] font-sans text-ink-faint mb-1.5 tracking-wider uppercase"
            >
              Confirm password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Re-enter password"
              className="input"
              disabled={loading || isValidToken === null}
            />
          </div>

          {error && (
            <div className="text-xs text-rose bg-rose-faint p-3 rounded-lg font-sans">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading || !password || !confirmPassword || isValidToken === null}
            className="btn-primary w-full"
          >
            {loading ? 'Updating...' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
}
